/**
 * Debug Results Panel
 *
 * Displays captured debug data including summary and payment-by-payment breakdown.
 */

import {
  Badge,
  Box,
  Card,
  HStack,
  SimpleGrid,
  Spinner,
  Table,
  Text,
  VStack,
} from '@chakra-ui/react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { type FC, useCallback, useMemo, useState } from 'react';
import { LAYOUT_SPACING } from '~/components/layout/shared/spacing';
import { CopyToClipboardButton } from '~/components/ui';
import type { DebugSessionCapture, PaymentSnapshot } from '~/services/debug-system';
import type { Candidate } from '~/types';
import { formatMarkdownTable } from '~/utils/markdown-table-formatter';

interface DebugResultsPanelProps {
  capture: DebugSessionCapture | null;
  isProcessing: boolean;
  error: string | null;
  candidateName?: string;
  processedCandidate?: Candidate | null;
}

export const DebugResultsPanel: FC<DebugResultsPanelProps> = ({
  capture,
  isProcessing,
  error,
  candidateName,
  processedCandidate,
}) => {
  // Loading state
  if (isProcessing) {
    return (
      <Box p={8} textAlign="center">
        <Spinner size="lg" mb={4} />
        <Text>Processing {candidateName}...</Text>
      </Box>
    );
  }

  // Error state
  if (error) {
    return (
      <Box p={6} bg="red.subtle" borderRadius="md">
        <Text color="red.fg" fontWeight="semibold">
          Processing Error
        </Text>
        <Text color="red.fg" fontSize="sm">
          {error}
        </Text>
      </Box>
    );
  }

  // No capture yet
  if (!capture) {
    return null;
  }

  return (
    <VStack align="stretch" gap={LAYOUT_SPACING.SECTION_GAP}>
      {/* Summary Card */}
      <DebugSummaryCard capture={capture} processedCandidate={processedCandidate} />

      {/* Payment Table */}
      <PaymentByPaymentTable capture={capture} />
    </VStack>
  );
};

/**
 * Summary Card Component
 */
interface DebugSummaryCardProps {
  capture: DebugSessionCapture;
  processedCandidate?: Candidate | null;
}

const DebugSummaryCard: FC<DebugSummaryCardProps> = ({ capture, processedCandidate }) => {
  const { summary } = capture;

  // Use processed candidate results if available, otherwise fall back to capture summary
  const summaryData = useMemo(() => {
    if (processedCandidate?.results) {
      const cashFlows = processedCandidate.cashFlows || [];
      const endingDRF =
        cashFlows.length > 0 ? (cashFlows[cashFlows.length - 1]?.drfEndingBalance ?? 0) : 0;

      return {
        totalRevenue: processedCandidate.results.totalRevenue,
        totalPrincipal: processedCandidate.results.totalPrincipal,
        totalInterest: processedCandidate.results.totalInterest,
        endingDRF,
        isViable: processedCandidate.results.isViable,
      };
    }

    // Fall back to capture summary (point-in-time debug data)
    return {
      totalRevenue: summary.totalRevenue,
      totalPrincipal: summary.totalPrincipal,
      totalInterest: summary.totalInterest,
      endingDRF: summary.endingDRF,
      isViable: summary.isViable,
    };
  }, [processedCandidate, summary]);

  return (
    <Card.Root>
      <Card.Body>
        <HStack justify="space-between" align="center" gap={8} width="100%">
          {/* Left column: Candidate info and status */}
          <VStack align="start" gap={2} flexShrink={0}>
            <HStack gap={2} align="center">
              <Text fontWeight="semibold">{capture.candidateName}</Text>
              <Badge colorPalette={summaryData.isViable ? 'green' : 'red'} size="lg">
                {summaryData.isViable ? 'Viable' : 'Failed'}
              </Badge>
            </HStack>
            <VStack align="start" gap={0}>
              <Text fontSize="xs" color="fg.muted">
                Processed in {capture.processingTimeMs.toFixed(2)}ms
              </Text>
              <Text fontSize="xs" color="fg.muted">
                {summary.paymentsProcessed} payments captured
              </Text>
            </VStack>
          </VStack>

          {/* Right column: Financial metrics - evenly spread across remaining space */}
          <HStack flex="1" justify="space-between" align="flex-start" pl={8} minW={0}>
            <SummaryMetric
              label="Total Revenue"
              value={`$${summaryData.totalRevenue.toLocaleString()}`}
            />
            <SummaryMetric
              label="Total Principal"
              value={`$${summaryData.totalPrincipal.toLocaleString()}`}
            />
            <SummaryMetric
              label="Total Interest"
              value={`$${summaryData.totalInterest.toLocaleString()}`}
            />
            <SummaryMetric
              label="Ending DRF"
              value={`$${summaryData.endingDRF.toLocaleString()}`}
            />
          </HStack>
        </HStack>
      </Card.Body>
    </Card.Root>
  );
};

interface SummaryMetricProps {
  label: string;
  value: string;
}

const SummaryMetric: FC<SummaryMetricProps> = ({ label, value }) => (
  <VStack align="start" gap={0}>
    <Text fontSize="xs" color="fg.muted">
      {label}
    </Text>
    <Text fontWeight="semibold">{value}</Text>
  </VStack>
);

/**
 * Payment by Payment Table
 */
interface PaymentByPaymentTableProps {
  capture: DebugSessionCapture;
}

const PaymentByPaymentTable: FC<PaymentByPaymentTableProps> = ({ capture }) => {
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());

  const toggleRow = useCallback((index: number) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  }, []);

  // Sort payments by date (ascending)
  const sortedPayments = [...capture.paymentSnapshots].sort(
    (a, b) => new Date(a.paymentDate).getTime() - new Date(b.paymentDate).getTime()
  );

  return (
    <Card.Root>
      <Card.Header>
        <Text fontWeight="semibold">Payment-by-Payment Breakdown</Text>
        <Text fontSize="xs" color="fg.muted">
          Click a row to expand and see calculation details
        </Text>
      </Card.Header>
      <Card.Body p={0}>
        <Box overflowX="auto">
          <Table.Root size="sm">
            <Table.Header>
              <Table.Row>
                <Table.ColumnHeader w="40px" />
                <Table.ColumnHeader>Date</Table.ColumnHeader>
                <Table.ColumnHeader>Tax Year</Table.ColumnHeader>
                <Table.ColumnHeader textAlign="right">Revenue</Table.ColumnHeader>
                <Table.ColumnHeader textAlign="right">Available</Table.ColumnHeader>
                <Table.ColumnHeader textAlign="right">Principal</Table.ColumnHeader>
                <Table.ColumnHeader textAlign="right">Interest</Table.ColumnHeader>
                <Table.ColumnHeader textAlign="right">DRF End</Table.ColumnHeader>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {sortedPayments.map((snapshot, index) => (
                <PaymentRow
                  key={`${snapshot.paymentDate}-${index}`}
                  snapshot={snapshot}
                  index={index}
                  isExpanded={expandedRows.has(index)}
                  onToggle={() => toggleRow(index)}
                />
              ))}
            </Table.Body>
          </Table.Root>
        </Box>
      </Card.Body>
    </Card.Root>
  );
};

interface PaymentRowProps {
  snapshot: PaymentSnapshot;
  index: number;
  isExpanded: boolean;
  onToggle: () => void;
}

const PaymentRow: FC<PaymentRowProps> = ({ snapshot, isExpanded, onToggle }) => {
  const getFieldValue = (fieldId: string) => {
    const field = snapshot.fields[fieldId];
    return field?.formatted ?? 'N/A';
  };

  return (
    <>
      {/* Main row */}
      <Table.Row
        cursor="pointer"
        onClick={onToggle}
        bg={snapshot.isInterestOnly ? 'orange.subtle' : undefined}
        _hover={{ bg: 'bg.subtle' }}
      >
        <Table.Cell>
          {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        </Table.Cell>
        <Table.Cell>
          <HStack gap={2}>
            <Text>{snapshot.paymentDate}</Text>
            {snapshot.isInterestOnly && (
              <Badge size="sm" colorPalette="orange">
                I-only
              </Badge>
            )}
          </HStack>
        </Table.Cell>
        <Table.Cell>{snapshot.taxYear}</Table.Cell>
        <Table.Cell textAlign="right">{getFieldValue('revenue.paymentPortion')}</Table.Cell>
        <Table.Cell textAlign="right">{getFieldValue('revenue.availableForBonds')}</Table.Cell>
        <Table.Cell textAlign="right">{getFieldValue('principal.total')}</Table.Cell>
        <Table.Cell textAlign="right">{getFieldValue('interest.total')}</Table.Cell>
        <Table.Cell textAlign="right">{getFieldValue('drf.ending')}</Table.Cell>
      </Table.Row>

      {/* Expanded detail row */}
      {isExpanded && (
        <Table.Row>
          <Table.Cell colSpan={8} p={0}>
            <CalculationBreakdownPanel snapshot={snapshot} />
          </Table.Cell>
        </Table.Row>
      )}
    </>
  );
};

/**
 * Calculation Breakdown Panel (shown when row is expanded)
 */
interface CalculationBreakdownPanelProps {
  snapshot: PaymentSnapshot;
}

const CalculationBreakdownPanel: FC<CalculationBreakdownPanelProps> = ({ snapshot }) => {
  // Build the payment data text
  const buildPaymentText = useCallback(() => {
    const lines: string[] = [`Payment Date: ${snapshot.paymentDate}`, ''];

    for (const [_fieldId, field] of Object.entries(snapshot.fields)) {
      lines.push(`${field.fieldName}: ${field.formatted}`);
      if (field.formula) {
        lines.push(`  ${field.formula}`);
      }
    }

    return lines.join('\n');
  }, [snapshot]);

  // Regular copy - just the data
  const getPaymentText = useCallback(() => {
    return buildPaymentText();
  }, [buildPaymentText]);

  // Copy with LLM prompt - includes instruction to review cashflows file
  const getPaymentTextWithPrompt = useCallback(() => {
    return `Please review the most recent cashflows file in apps/web/debug-output/ as you analyze this payment data.

---

${buildPaymentText()}`;
  }, [buildPaymentText]);

  // Generate bond breakdown table text for copying
  const getBondBreakdownText = useCallback(() => {
    const activeBonds = snapshot.bondBreakdown?.filter((b) => b.isActive) || [];

    const headers = [
      'Bond',
      'Principal',
      'Rate',
      'New Interest',
      'Future Oblig.',
      'Total Interest',
      'Total Payment',
    ];

    const rows = activeBonds.map((bond) => [
      bond.bondSeriesName,
      bond.fields['bond.principal']?.formatted ?? 'N/A',
      bond.fields['bond.interestRate']?.formatted ?? 'N/A',
      bond.fields['bond.newInterest']?.formatted ?? 'N/A',
      bond.fields['bond.futureInterestObligation']?.formatted ?? 'N/A',
      bond.fields['bond.totalInterest']?.formatted ?? 'N/A',
      bond.fields['bond.totalPayment']?.formatted ?? 'N/A',
    ]);

    const markdownTable = formatMarkdownTable(headers, rows, {
      align: 'auto',
      minColumnWidth: 3,
      maxColumnWidth: 50,
    });

    return `Per-Bond Breakdown for ${snapshot.paymentDate}\n\n${markdownTable}`;
  }, [snapshot]);

  return (
    <Box p={4} bg="bg.subtle" borderTopWidth="1px">
      <HStack justify="space-between" mb={4}>
        <Text fontWeight="semibold" fontSize="sm">
          Calculation Details for {snapshot.paymentDate}
        </Text>
        <HStack gap={1}>
          <CopyToClipboardButton
            size="xs"
            variant="ghost"
            textToCopy={getPaymentText}
            title="Copy payment data"
          >
            Copy
          </CopyToClipboardButton>
          <CopyToClipboardButton
            size="xs"
            variant="ghost"
            colorPalette="purple"
            textToCopy={getPaymentTextWithPrompt}
            title="Copy with LLM prompt to review cashflows file"
          >
            + Prompt
          </CopyToClipboardButton>
        </HStack>
      </HStack>

      <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} gap={4}>
        {/* Revenue Source - The ingredients for revenue calculation */}
        <BreakdownSection
          title="Revenue Source"
          fields={[
            { id: 'revenue.taxableValue', snapshot },
            { id: 'revenue.millageRate', snapshot },
            { id: 'revenue.totalForTaxYear', snapshot },
            { id: 'revenue.paymentPercentage', snapshot },
            { id: 'revenue.paymentPortion', snapshot },
          ]}
        />

        {/* Revenue Flow - How revenue becomes available */}
        <BreakdownSection
          title="Revenue Flow"
          fields={[
            { id: 'revenue.existingDebtPayment', snapshot },
            { id: 'revenue.minusExistingDebt', snapshot },
            { id: 'interest.obligation', snapshot },
            { id: 'revenue.availableForBonds', snapshot },
          ]}
        />

        {/* Revenue Carry-Forward - Where extra revenue comes from */}
        <BreakdownSection
          title="I/O Revenue Carry-Forward"
          fields={[
            { id: 'revenue.nextPaymentIsInterestOnly', snapshot },
            { id: 'revenue.nextPeriodAvailable', snapshot },
            { id: 'revenue.combinedForDistribution', snapshot },
          ]}
        />

        {/* Principal Derivation - How we calculate principal from available revenue */}
        <BreakdownSection
          title="Principal Derivation"
          fields={[
            { id: 'principal.maxInterestRate', snapshot },
            { id: 'principal.interestDivisor', snapshot },
            { id: 'principal.rawPrincipalCapacity', snapshot },
            { id: 'principal.roundedPrincipal', snapshot },
          ]}
        />

        {/* Interest Details */}
        <BreakdownSection
          title="Interest Details"
          fields={[
            { id: 'interest.totalNewInterest', snapshot },
            { id: 'interest.totalFutureObligation', snapshot },
            { id: 'interest.total', snapshot },
            { id: 'interest.activeBondCount', snapshot },
          ]}
        />

        {/* Principal Distribution */}
        <BreakdownSection
          title="Principal"
          fields={[
            { id: 'principal.total', snapshot },
            { id: 'principal.totalBondPayment', snapshot },
          ]}
        />

        {/* DRF Tracking - Note: Final values calculated after all payments processed */}
        <BreakdownSection
          title="DRF (Debt Retirement Fund)"
          fields={[
            { id: 'drf.starting', snapshot },
            { id: 'drf.ending', snapshot },
            { id: 'drf.netChange', snapshot },
          ]}
        />
      </SimpleGrid>

      {/* Bond Breakdown */}
      {snapshot.bondBreakdown && snapshot.bondBreakdown.length > 0 && (
        <Box mt={4}>
          <HStack justify="space-between" align="center" mb={2}>
            <Text fontWeight="semibold" fontSize="sm">
              Per-Bond Breakdown
            </Text>
            <CopyToClipboardButton
              size="xs"
              variant="ghost"
              textToCopy={getBondBreakdownText}
              title="Copy bond breakdown table"
            >
              Copy
            </CopyToClipboardButton>
          </HStack>
          <Table.Root size="sm">
            <Table.Header>
              <Table.Row>
                <Table.ColumnHeader>Bond</Table.ColumnHeader>
                <Table.ColumnHeader textAlign="right">Principal</Table.ColumnHeader>
                <Table.ColumnHeader textAlign="right">Rate</Table.ColumnHeader>
                <Table.ColumnHeader textAlign="right">New Interest</Table.ColumnHeader>
                <Table.ColumnHeader textAlign="right">Future Oblig.</Table.ColumnHeader>
                <Table.ColumnHeader textAlign="right">Total Interest</Table.ColumnHeader>
                <Table.ColumnHeader textAlign="right">Total Payment</Table.ColumnHeader>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {snapshot.bondBreakdown
                .filter((b) => b.isActive)
                .map((bond) => (
                  <Table.Row key={bond.bondSeriesId}>
                    <Table.Cell>{bond.bondSeriesName}</Table.Cell>
                    <Table.Cell textAlign="right">
                      {bond.fields['bond.principal']?.formatted ?? 'N/A'}
                    </Table.Cell>
                    <Table.Cell textAlign="right">
                      {bond.fields['bond.interestRate']?.formatted ?? 'N/A'}
                    </Table.Cell>
                    <Table.Cell textAlign="right">
                      {bond.fields['bond.newInterest']?.formatted ?? 'N/A'}
                    </Table.Cell>
                    <Table.Cell textAlign="right">
                      {bond.fields['bond.futureInterestObligation']?.formatted ?? 'N/A'}
                    </Table.Cell>
                    <Table.Cell textAlign="right">
                      {bond.fields['bond.totalInterest']?.formatted ?? 'N/A'}
                    </Table.Cell>
                    <Table.Cell textAlign="right">
                      {bond.fields['bond.totalPayment']?.formatted ?? 'N/A'}
                    </Table.Cell>
                  </Table.Row>
                ))}
            </Table.Body>
          </Table.Root>
        </Box>
      )}
    </Box>
  );
};

interface BreakdownSectionProps {
  title: string;
  fields: Array<{ id: string; snapshot: PaymentSnapshot }>;
}

const BreakdownSection: FC<BreakdownSectionProps> = ({ title, fields }) => {
  // Generate text representation of this section for copying
  const getSectionText = useCallback(() => {
    const lines: string[] = [`${title}`, ''];

    fields.forEach(({ id, snapshot }) => {
      const field = snapshot.fields[id];
      if (!field) return;

      lines.push(`${field.fieldName}: ${field.formatted}`);
      if (field.formula) {
        lines.push(`  ${field.formula}`);
      }
    });

    return lines.join('\n');
  }, [title, fields]);

  return (
    <Box>
      <HStack justify="space-between" align="center" mb={2}>
        <Text fontWeight="semibold" fontSize="xs" color="fg.muted">
          {title}
        </Text>
        <CopyToClipboardButton
          size="xs"
          variant="ghost"
          textToCopy={getSectionText}
          title={`Copy ${title} data`}
        >
          Copy
        </CopyToClipboardButton>
      </HStack>
      <VStack align="stretch" gap={1}>
        {fields.map(({ id, snapshot }) => {
          const field = snapshot.fields[id];
          if (!field) return null;

          return (
            <Box key={id} p={2} bg="bg.panel" borderRadius="sm">
              <HStack justify="space-between">
                <Text fontSize="xs" color="fg.muted">
                  {field.fieldName}
                </Text>
                <Text fontSize="sm" fontWeight="medium">
                  {field.formatted}
                </Text>
              </HStack>
              {field.formula && (
                <Text fontSize="xs" color="fg.muted" mt={1}>
                  {field.formula}
                </Text>
              )}
            </Box>
          );
        })}
      </VStack>
    </Box>
  );
};
